import { LoadingState } from '@common/components';
import { FEATURE_FLAGS_QUERY } from '@common/graphql';
import { useQuery } from '@common/hooks';
import React, {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';

const FLAG_NAMES: string[] = [
  'bulk_shift_incentive_overrides',
  'load_balancing_availability_signals',
  'load_balancing_display_holidays',
  'load_balancing_fte_tolerance_band',
  'load_balancing_handoff_times',
  'load_balancing_review_declutter',
  'scheduling_include_exclude',
  'workload_expression',
  'moment_shadow_mode',
  'moment_shadow_match_samples',
  'shift_gap_fill_rate_display',
  'shift_sets_ingestion',
  // TODO(WORK-6503): debug-only flag — remove when the debug Review Team behavior is torn down.
  'team_review_period_picker',
  'vms_timesheet_upload',
];

const FLAG_DEFAULTS = buildFlagDefaults(FLAG_NAMES);

type FeatureFlags = typeof FLAG_DEFAULTS;

type SnakeToPascal<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToPascal<U>>}`
  : S;

type TransformedFlags = {
  [K in keyof FeatureFlags as `is${Capitalize<SnakeToPascal<K & string>>}Enabled`]: FeatureFlags[K];
};

const transformSnakeToPascalCase = (key: string) => {
  return key
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    .replace(/^[a-z]/, (letter) => letter.toUpperCase());
};

const transformFlagsToContext = (flags: FeatureFlags): TransformedFlags => {
  const camelFlags = {} as TransformedFlags;
  for (const key in flags) {
    if (!Object.prototype.hasOwnProperty.call(flags, key)) continue;
    const pascalCaseKey = transformSnakeToPascalCase(key);
    (camelFlags as Record<string, boolean>)[`is${pascalCaseKey}Enabled`] =
      flags[key as keyof FeatureFlags];
  }
  return camelFlags;
};

const FeatureFlagsContext = createContext<TransformedFlags>(
  transformFlagsToContext(FLAG_DEFAULTS)
);

export const FeatureFlagsProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const hasFlags = FLAG_NAMES.length > 0;

  const query = useQuery(FEATURE_FLAGS_QUERY, {
    variables: {
      flagNames: FLAG_NAMES,
    },
    skip: !hasFlags,
  });

  const effectiveFlags = useMemo(() => {
    const flags = {
      ...FLAG_DEFAULTS,
      ...((query.data?.featureFlags || {}) as FeatureFlags),
    };

    return transformFlagsToContext(flags);
  }, [query.data]);

  if (hasFlags && query.loading && !query.data) {
    return <LoadingState />;
  }

  return (
    <FeatureFlagsContext.Provider value={effectiveFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): TransformedFlags =>
  useContext(FeatureFlagsContext);

function buildFlagDefaults(flagNames: string[]) {
  return flagNames.reduce(
    (acc, flagName) => {
      acc[flagName] = false;
      return acc;
    },
    {} as Record<string, boolean>
  );
}
